/**
 * Porter stemmer (Porter1) — pure-function port.
 *
 * Why Porter1 and not Porter2: Porter1 is ~80 LoC of well-defined rules with
 * a known specification (Porter, 1980, "An algorithm for suffix stripping").
 * For a ~100-tour corpus the relevance gap vs. Porter2 is invisible, and the
 * smaller surface area means fewer places for a port bug to hide. If we ever
 * need Porter2, this module's `stem(word): string` contract stays unchanged.
 *
 * The implementation below follows the canonical algorithm description
 * step-for-step. Variable names mirror the paper (m, vowel, consonant) so
 * that anyone cross-referencing the spec can read this directly.
 */

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

function isConsonant(word: string, i: number): boolean {
  const ch = word[i];
  if (VOWELS.has(ch)) return false;
  if (ch === 'y') return i === 0 ? true : !isConsonant(word, i - 1);
  return true;
}

/**
 * Measure m: the count of vowel-consonant sequences in the stem.
 * Used by every step to decide whether a suffix may be stripped.
 */
function measure(stem: string): number {
  let m = 0;
  let i = 0;
  const n = stem.length;
  // skip leading consonants
  while (i < n && isConsonant(stem, i)) i++;
  while (i < n) {
    while (i < n && !isConsonant(stem, i)) i++;
    if (i >= n) break;
    m++;
    while (i < n && isConsonant(stem, i)) i++;
  }
  return m;
}

function containsVowel(stem: string): boolean {
  for (let i = 0; i < stem.length; i++) if (!isConsonant(stem, i)) return true;
  return false;
}

function endsDoubleConsonant(stem: string): boolean {
  const n = stem.length;
  if (n < 2) return false;
  if (stem[n - 1] !== stem[n - 2]) return false;
  return isConsonant(stem, n - 1);
}

function endsCVC(stem: string): boolean {
  const n = stem.length;
  if (n < 3) return false;
  if (
    !isConsonant(stem, n - 3) ||
    isConsonant(stem, n - 2) ||
    !isConsonant(stem, n - 1)
  )
    return false;
  const last = stem[n - 1];
  return last !== 'w' && last !== 'x' && last !== 'y';
}

function replaceSuffix(word: string, suffix: string, replacement: string): string {
  return word.slice(0, word.length - suffix.length) + replacement;
}

/* ───────────────────────────── steps ───────────────────────────── */

function step1a(w: string): string {
  if (w.endsWith('sses')) return replaceSuffix(w, 'sses', 'ss');
  if (w.endsWith('ies')) return replaceSuffix(w, 'ies', 'i');
  if (w.endsWith('ss')) return w;
  if (w.endsWith('s')) return w.slice(0, -1);
  return w;
}

function step1b(w: string): string {
  if (w.endsWith('eed')) {
    const stem = w.slice(0, -3);
    return measure(stem) > 0 ? stem + 'ee' : w;
  }
  let stem: string | null = null;
  if (w.endsWith('ed') && containsVowel(w.slice(0, -2))) stem = w.slice(0, -2);
  else if (w.endsWith('ing') && containsVowel(w.slice(0, -3))) stem = w.slice(0, -3);
  if (stem === null) return w;

  if (stem.endsWith('at') || stem.endsWith('bl') || stem.endsWith('iz')) return stem + 'e';
  if (endsDoubleConsonant(stem)) {
    const last = stem[stem.length - 1];
    if (last !== 'l' && last !== 's' && last !== 'z') return stem.slice(0, -1);
    return stem;
  }
  if (measure(stem) === 1 && endsCVC(stem)) return stem + 'e';
  return stem;
}

function step1c(w: string): string {
  if (w.endsWith('y') && containsVowel(w.slice(0, -1))) return w.slice(0, -1) + 'i';
  return w;
}

const STEP2: ReadonlyArray<readonly [string, string]> = [
  ['ational', 'ate'], ['tional', 'tion'], ['enci', 'ence'], ['anci', 'ance'],
  ['izer', 'ize'], ['abli', 'able'], ['alli', 'al'], ['entli', 'ent'],
  ['eli', 'e'], ['ousli', 'ous'], ['ization', 'ize'], ['ation', 'ate'],
  ['ator', 'ate'], ['alism', 'al'], ['iveness', 'ive'], ['fulness', 'ful'],
  ['ousness', 'ous'], ['aliti', 'al'], ['iviti', 'ive'], ['biliti', 'ble'],
];

function step2(w: string): string {
  for (const [suffix, replacement] of STEP2) {
    if (w.endsWith(suffix)) {
      const stem = w.slice(0, -suffix.length);
      return measure(stem) > 0 ? stem + replacement : w;
    }
  }
  return w;
}

const STEP3: ReadonlyArray<readonly [string, string]> = [
  ['icate', 'ic'], ['ative', ''], ['alize', 'al'], ['iciti', 'ic'],
  ['ical', 'ic'], ['ful', ''], ['ness', ''],
];

function step3(w: string): string {
  for (const [suffix, replacement] of STEP3) {
    if (w.endsWith(suffix)) {
      const stem = w.slice(0, -suffix.length);
      return measure(stem) > 0 ? stem + replacement : w;
    }
  }
  return w;
}

const STEP4: ReadonlyArray<string> = [
  'al', 'ance', 'ence', 'er', 'ic', 'able', 'ible', 'ant', 'ement',
  'ment', 'ent', 'ou', 'ism', 'ate', 'iti', 'ous', 'ive', 'ize',
];

function step4(w: string): string {
  // Special: -ion only strips after s or t
  if (w.endsWith('ion')) {
    const stem = w.slice(0, -3);
    const last = stem[stem.length - 1];
    if ((last === 's' || last === 't') && measure(stem) > 1) return stem;
  }
  for (const suffix of STEP4) {
    if (w.endsWith(suffix)) {
      const stem = w.slice(0, -suffix.length);
      if (measure(stem) > 1) return stem;
      return w;
    }
  }
  return w;
}

function step5a(w: string): string {
  if (!w.endsWith('e')) return w;
  const stem = w.slice(0, -1);
  const m = measure(stem);
  if (m > 1) return stem;
  if (m === 1 && !endsCVC(stem)) return stem;
  return w;
}

function step5b(w: string): string {
  if (
    measure(w) > 1 &&
    endsDoubleConsonant(w) &&
    w[w.length - 1] === 'l'
  ) {
    return w.slice(0, -1);
  }
  return w;
}

/**
 * Reduce a token to its Porter1 stem. Pure, deterministic, no allocation
 * beyond the strings the language already requires for slicing.
 *
 * Words shorter than 3 characters are returned unchanged — Porter is unstable
 * on them and the BM25 IDF will demote them anyway.
 */
export function stem(word: string): string {
  if (word.length <= 2) return word;
  let w = word;
  w = step1a(w);
  w = step1b(w);
  w = step1c(w);
  w = step2(w);
  w = step3(w);
  w = step4(w);
  w = step5a(w);
  w = step5b(w);
  return w;
}
